import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { OidcSecurityService } from '../../auth/services/oidc.security.service';
import { SheetService } from '../../shared/sheet-export-import/sheet.service';
import { MULTICOLOR_FULL, BLUE_FULL, DOTS_FULL, PreloaderHandler } from '../../shared/preloader/preloader-handler';
import { CheckboxModel } from '../../shared/models/CheckboxModel';
import { NotificationHandlerService, ReportService, LoadingService } from '../../index';
import { ImportColumnCheckboxModel } from '../../shared/models/ImportColumnCheckboxModel';
import { UnitSimCancelledModel } from '../../shared/models/cancelled-related/UnitSimCancelledModel';

@Injectable()
export class ReportCancelledUnitBackgroundService {

    private MY_NAME = 'REPORT_CANCELLED_BG_SERVICE';

    // Unit types & array of all types
    private UNIT_NEW = 'New';
    private UNIT_ACTIVE = 'Active';
    private UNIT_INACTIVE = 'Inactive';
    private UNIT_CANCELLED = 'Cancelled';
    private allReportTypes: string[];

    // Used to remember selection when running long reports
    // This data is broadcast back from sheet service
    private rememberCols = false;
    private rememberSelectedColumns: ImportColumnCheckboxModel[];

    // Used to allow subscribing from parent component to show when a report is running
    public isRunningReports = new BehaviorSubject<boolean>(false);

    // All selected report sheets (passed when starting report)
    private selectedCancellations: CheckboxModel[];
    private selectedCancellationName: string;

    // Report name and returned data
    private reportName: string;
    private reportData: any = [];

    // Used to identify whether the current report has been cancelled
    private isCancelled: boolean;

    private actionUrl: string;
    private headers: Headers;
    private uData: any;

    constructor(private _sheetService: SheetService,
        private _notificationService: NotificationHandlerService,
        private _loadingService: LoadingService,
        private _reportService: ReportService,
        public oidcSecurityService: OidcSecurityService) {
        this.isCancelled = false;
        this.allReportTypes = [
            this.UNIT_ACTIVE,
            this.UNIT_INACTIVE,
            this.UNIT_CANCELLED,
            this.UNIT_NEW
        ];

        this._sheetService.sheetColumnSelection.subscribe((data) => {
            if (data instanceof Array) {
                
                // Set broadcasted parameters
                // 0 -- Remember selection of columns
                // 1 -- All selected column objects
                // 2 -- Whether the user cancelled report from column select
                // 3 -- The name of the caller e.g. the component or service which made the call
                this.rememberCols = data[0];
                this.rememberSelectedColumns = data[1];
                //If user clicked cancel
                if (data[2]) {
                    // Cancel report
                    this.reportCancelled();
                } else {
                    // Generate report
                    if (data[3] == this.MY_NAME) {
                        this.generateReport();
                    }
                }
            }
        });
    }

    // Broabcast the running status of the report
    private updateRunningStatus(status: boolean): void {
        this.isRunningReports.next(status);
    }

    // Setup variables used to run the reports & start the report
    public readyReport(selectedCancellations: CheckboxModel[]): void {
        this.isCancelled = false;
        this.selectedCancellations = selectedCancellations;
        this.updateRunningStatus(true);
        this._loadingService.itemLoading();
        this.runReport();
    }

    // Cancel current running report
    public reportCancelled(): void {
        this._notificationService.logToastWarning('Report cancelled...');
        this.isCancelled = true;
        this.resetReportProperties();
    }

    // Current report has completed
    public reportComplete(): void {
        this._notificationService.logToastSuccess('Report complete...');
        this.resetReportProperties();
    }

    // Reset used variables to default after completing or cancelling
    private resetReportProperties(): void {
        this.updateRunningStatus(false);
        this._loadingService.itemLoaded();
        this.selectedCancellations = [];
    }

    // Grab a company out the list and start runnning report on it
    private runReport(): void {
        this.selectedCancellationName = this.selectedCancellations.find(cbx => cbx.value == true).name;
        this._notificationService.logToastInfo('Running report on ' + this.selectedCancellationName + '...');
        this.getNextSheets();
    }

    // Get all selected reports from the api, wait for them to complete then continue
    private getNextSheets(): void {
        if (!this.isCancelled) {
            this._reportService.GetCancelledNonProcessedReport().subscribe((data) => {
                this.reportData = data;
                this.getUserSelection();
            }, (error) => {
                this._notificationService.logError(error);
            });
        }
    }

    // Prompt user and get selection of columns
    private getUserSelection(): void {
        if (this.rememberCols) {
            this.generateReport();
        } else {
            let dummy = this.getDummyModel();
            // Pass a dummy object to column select component incase returned data has no objects
            // Pass a blank sheet name as we expect the selcted columns to come back before being added
            // Pass true, indicating we want the selection to be broadcast back instead of automatically adding sheet
            this._sheetService.promptColumnSelect(dummy, this.selectedCancellationName, true, this.MY_NAME);
        }
    }

    // Add all data to new sheets then initiate workbook download
    private generateReport(): void {
        if (!this.isCancelled) {
            let reports = [];
            reports.push(this._sheetService.addSheet(this.reportData, this.rememberSelectedColumns, this.getReportName()));

            Promise.all(reports).then(() => {
                this.downloadReport().then(() => {
                    this.reportComplete();
                });
            });
        }
    }

    // Download workbook then check if there are any more companies
    private downloadReport(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this._sheetService.downloadWorkbook(this.getReportName()).then(() => {
                resolve(true);
            });
        });
    }

    // Get report name from current company and report date
    private getReportName(): string {
        let name = this.selectedCancellationName + '_' + this.getDateStr(new Date());
        return name;
    }

    // Convert a date object to a month name and full year string
    private getDateStr(date: Date): string {
        date = new Date(date);
        let months = [' Jan ', ' Feb ', ' Mar ', ' Apr ', ' May ', ' Jun ',
            ' Jul ', ' Aug ', ' Sep ', ' Oct ', ' Nov ', ' Dec '];
        return (months[date.getMonth()].toUpperCase() + date.getFullYear());
    }

    // Return a model of report objects with dummy data
    private getDummyModel(): UnitSimCancelledModel[] {
        let dummyUnit: UnitSimCancelledModel = {
            UnitId: '',
            Product: '',
            MonitoringCentre: '',
            Bureau: '',
            UnitPlan: '',
            UnitFirmware: '',
            AccountNo: '',
            SimId: '',
            Apn: '',
            SimType: '',
            MobileNo: '',
            SimNo: '',
            IP: '',
            Processed: false,
            DateLoaded: new Date(),
            DateEdited: new Date()
        };
        return [dummyUnit];
    }

}


// WEBPACK FOOTER //
// ./angularApp/app/tools/report-cancelled-unit/report-cancelled-background.service.ts